import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import {
  useAgregarFila,
  useCorregirFila,
  useGuardarLaPestana,
  useMantenerFila,
  useQuitarFila,
  useQuitarFotoDeLaFila,
  useRotularFotoDeLaFila,
  useSubirFotoDeLaFila,
} from "@/hooks/useInformes";
import { registroDeTerrenoKey } from "@/hooks/useRegistroDeTerreno";
import { getApiErrorMessage } from "@/lib/api";
import type {
  CamposDeLaFila,
  GuardarLaPestanaInput,
  Informe,
  PestanaConCuadro,
} from "@/lib/informes";

/** Lo que una pestaña con cuadro necesita para funcionar. */
export interface CuadroDelInforme {
  /** El "Guardar cambios" de la pestaña está en vuelo. */
  guardando: boolean;
  /** Hay una operación de fila en vuelo (alta, corrección, borrado, foto). */
  ocupada: boolean;
  onAgregar: (campos: CamposDeLaFila, fotos: File[]) => void;
  onCorregir: (filaId: number, campos: CamposDeLaFila) => void;
  onQuitar: (filaId: number) => void;
  /** «Mantener» en el aviso de terreno «No está en el predio» (maquinaria e instalaciones). */
  onMantener: (filaId: number) => void;
  /**
   * Corrige VARIAS filas con los mismos campos, de a UNA y esperando cada
   * respuesta. En paralelo, cada PATCH devuelve la ficha ENTERA y la última en
   * llegar pisaría en la caché a las demás: la pantalla mostraría filas sin
   * validar que el servidor ya tiene validadas.
   */
  onCorregirVarias: (filaIds: number[], campos: CamposDeLaFila) => Promise<void>;
  /** Quita varias filas, de a una, por el mismo motivo. */
  onQuitarVarias: (filaIds: number[]) => Promise<void>;
  onSubirFoto: (filaId: number, archivo: File) => void;
  onQuitarFoto: (filaId: number, fotoId: number) => void;
  /** Escribe la leyenda libre de una foto; se guarda sola, no con «Guardar cambios». */
  onLeyendaDeFoto: (filaId: number, fotoId: number, leyenda: string | null) => void;
  onGuardar: (input: GuardarLaPestanaInput) => void;
}

interface Opciones {
  cuadro: PestanaConCuadro;
  informe: Informe;
  /**
   * Cómo se nombra una fila en los avisos: "La máquina", "La instalación".
   *
   * CON MAYÚSCULA INICIAL porque encabeza la frase ("La máquina se guardó") y
   * con artículo porque las tres tienen género distinto — pasarlo armado evita
   * un `if` sobre el género en cada toast.
   */
  laFila: string;
  /** El nombre de la pestaña en su aviso de guardado: "Construcciones". */
  laPestana: string;
  /** Qué hacer cuando el "Guardar cambios" de la pestaña salió bien. */
  alGuardar: () => void;
  /** El manejador de errores de la pantalla, que además cancela la salida. */
  alFallar: (error: unknown) => void;
}

/**
 * Todo el cableado de UNA pestaña con cuadro: las seis mutaciones, sus avisos
 * y el baile de subir las fotos después del alta.
 *
 * NACIÓ POR EXTRACCIÓN, con la cuarta y la quinta pestaña entrando de golpe:
 * el bloque de maquinaria ocupaba ~98 líneas dentro de `InformeEditar`, y
 * copiarlo dos veces habría llevado ese archivo de 1.000 a cerca de 1.450
 * líneas de cableado casi idéntico.
 *
 * `cuadro` ES A LA VEZ EL TRAMO DE LA URL Y LA CLAVE DE LA FICHA
 * (`informe.maquinarias`, `informe.instalaciones`, `informe.construcciones`),
 * y eso no es casualidad sino la condición que hace posible esto: si el
 * backend nombrara distinto la ruta y la relación, este hook necesitaría dos
 * parámetros y un mapa para unirlos.
 */
export function useCuadroDelInforme({
  cuadro,
  informe,
  laFila,
  laPestana,
  alGuardar,
  alFallar,
}: Opciones): CuadroDelInforme {
  const guardarLaPestana = useGuardarLaPestana(cuadro, informe.id);
  const agregar = useAgregarFila(cuadro, informe.id);
  const corregir = useCorregirFila(cuadro, informe.id);
  const quitar = useQuitarFila(cuadro, informe.id);
  const mantener = useMantenerFila(cuadro, informe.id);
  const subirFoto = useSubirFotoDeLaFila(cuadro, informe.id);
  const quitarFoto = useQuitarFotoDeLaFila(cuadro, informe.id);
  const rotularFoto = useRotularFotoDeLaFila(cuadro, informe.id);
  const queryClient = useQueryClient();

  /* Lo que cuelga del registro de terreno cambia cuando una fila nace de una
     construcción de la App o desaparece: la construcción de terreno pasa a
     estar enlazada, o vuelve a quedar suelta. Sin releerlo, la pantalla
     mostraría las dos —la fila y la suelta— hasta el próximo foco de ventana. */
  const releerElTerreno = () =>
    queryClient.invalidateQueries({ queryKey: registroDeTerrenoKey(informe.id) });

  return {
    guardando: guardarLaPestana.isPending,

    /* UNA SOLA BANDERA PARA TODO EL CUADRO, al revés que `imagenOcupada` de la
       pestaña de descripción. Allá cada recuadro es independiente y bloquear
       los cuatro por uno sería gratuito; acá toda escritura de fila devuelve la
       ficha ENTERA, así que mientras una está en vuelo el cuadro que se ve ya
       no es el que va a quedar — dejar otro botón vivo invita a apretarlo sobre
       datos que están por cambiar. */
    ocupada:
      agregar.isPending ||
      corregir.isPending ||
      quitar.isPending ||
      mantener.isPending ||
      subirFoto.isPending ||
      quitarFoto.isPending,

    onAgregar: (campos, fotos) =>
      agregar.mutate(campos, {
        onSuccess: (actualizado) => {
          toast.success(`${laFila} se guardó.`);

          if (campos.terreno_ref !== undefined) void releerElTerreno();

          /* LAS FOTOS VIAJAN DESPUÉS Y CON EL ID QUE DEVOLVIÓ EL ALTA: hasta
             este momento la fila no existía y no había a qué colgarlas. Es el
             mismo baile que la imagen satelital en el alta del informe.

             El id se busca por DIFERENCIA y no tomando la última de la lista:
             si alguien más agregó una fila mientras tanto, la última sería la
             suya y las fotos terminarían en el lugar equivocado. */
          const nueva = idDeLaFilaNueva(informe[cuadro] ?? [], actualizado[cuadro] ?? []);

          if (nueva === null || fotos.length === 0) return;

          fotos.forEach((archivo) =>
            subirFoto.mutate(
              { filaId: nueva, archivo },
              {
                /* LA FILA YA SE GUARDÓ: que falle una foto no puede leerse
                   como que el alta falló, o la persona la cargaría de nuevo y
                   quedaría duplicada. Por eso este error NO pasa por
                   `alFallar`, que además cancelaría la salida. */
                onError: (error) =>
                  toast.error(
                    getApiErrorMessage(
                      error,
                      `${laFila} se guardó, pero no pudimos subir “${archivo.name}”.`,
                    ),
                  ),
              },
            ),
          );
        },
        onError: alFallar,
      }),

    onCorregir: (filaId, campos) =>
      corregir.mutate(
        { filaId, campos },
        {
          onSuccess: () => {
            toast.success(`${laFila} se actualizó.`);

            // "Es la misma que": enlazar (o desenlazar) un registro de terreno
            // cambia qué cuelga de la fila y qué queda suelto.
            if (campos.terreno_ref !== undefined) void releerElTerreno();
          },
          onError: alFallar,
        },
      ),

    onQuitar: (filaId) =>
      quitar.mutate(filaId, {
        onSuccess: () => {
          toast.success(`${laFila} se quitó.`);
          void releerElTerreno();
        },
        onError: alFallar,
      }),

    onMantener: (filaId) =>
      mantener.mutate(filaId, {
        onSuccess: () => toast.success(`${laFila} se mantiene en el informe.`),
        onError: alFallar,
      }),

    onCorregirVarias: async (filaIds, campos) => {
      for (const filaId of filaIds) {
        try {
          await corregir.mutateAsync({ filaId, campos });
        } catch (error) {
          alFallar(error);

          return;
        }
      }

      toast.success(filaIds.length === 1 ? "1 fila actualizada." : `${filaIds.length} filas actualizadas.`);
    },

    onQuitarVarias: async (filaIds) => {
      for (const filaId of filaIds) {
        try {
          await quitar.mutateAsync(filaId);
        } catch (error) {
          alFallar(error);

          return;
        }
      }

      toast.success(filaIds.length === 1 ? "1 fila quitada." : `${filaIds.length} filas quitadas.`);
      void releerElTerreno();
    },

    onSubirFoto: (filaId, archivo) =>
      subirFoto.mutate(
        { filaId, archivo },
        {
          onSuccess: () => toast.success("Foto guardada."),
          onError: alFallar,
        },
      ),

    onQuitarFoto: (filaId, fotoId) =>
      quitarFoto.mutate(
        { filaId, fotoId },
        {
          onSuccess: () => toast.success("Foto quitada."),
          onError: alFallar,
        },
      ),

    /* NO entra en `ocupada`: la leyenda se escribe sin bloquear el cuadro (el
       campo vive en un modal abierto y deshabilitarlo a media escritura le
       quitaría el foco a quien sigue con la foto siguiente). */
    onLeyendaDeFoto: (filaId, fotoId, leyenda) =>
      rotularFoto.mutate(
        { filaId, fotoId, leyenda },
        {
          onSuccess: () => toast.success("Leyenda guardada."),
          onError: alFallar,
        },
      ),

    onGuardar: (input) =>
      guardarLaPestana.mutate(input, {
        onSuccess: () => {
          toast.success(`${laPestana} guardado.`);
          alGuardar();
        },
        onError: alFallar,
      }),
  };
}

/**
 * El id de la fila que acaba de nacer: la que está en la lista nueva y no
 * estaba en la anterior.
 *
 * POR DIFERENCIA Y NO TOMANDO LA ÚLTIMA, aunque la lista venga ordenada por
 * id: si alguien más agregó una fila a este informe mientras se cargaba esta,
 * la última sería la SUYA — y las fotos terminarían colgadas del lugar
 * equivocado, sin que nada falle.
 *
 * `null` cuando no hay ninguna nueva, que es un caso que no debería ocurrir
 * después de un alta exitosa. Quien llama lo trata como "no hay dónde colgar
 * las fotos" en vez de adivinar.
 */
function idDeLaFilaNueva(antes: { id: number }[], despues: { id: number }[]): number | null {
  const previas = new Set(antes.map((f) => f.id));

  return despues.find((f) => !previas.has(f.id))?.id ?? null;
}
